import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import type { Session } from '@benzinga/session';
import { TrendingTopic, TrendingTopicsManager } from '@benzinga/trending-topics-manager';
import { enrichTrendingTickerLogos } from './trending-logos';
import { Ticker } from '@benzinga/session';

export interface UseTrendingResult {
  trendingTickers: Ticker[];
  trendingTopics: TrendingTopic[];
  isLoading: boolean;
  hasError: boolean;
  ensureLoaded: () => void;
}

export const useTrending = (initialTrendingTopics?: TrendingTopic[]): UseTrendingResult => {
  const session = useContext(SessionContext);

  const seedTickerTopics = useMemo(
    () => (initialTrendingTopics ?? []).filter(topic => topic.taxonomy === 'tickers'),
    [initialTrendingTopics],
  );
  const seedTickers = useMemo<Ticker[]>(
    () => seedTickerTopics.map(topic => ({ name: topic.name, symbol: topic.name }) as Ticker),
    [seedTickerTopics],
  );
  const seedTopics = useMemo<TrendingTopic[]>(
    () => (initialTrendingTopics ?? []).filter(topic => topic.taxonomy !== 'tickers'),
    [initialTrendingTopics],
  );

  const [trendingTickers, setTrendingTickers] = useState<Ticker[]>(seedTickers);
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>(seedTopics);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasLoadedRef = useRef(false);
  const pendingLoadRef = useRef<Promise<void> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setTrendingTickers(seedTickers);
    setTrendingTopics(seedTopics);
    hasLoadedRef.current = false;
    pendingLoadRef.current = null;
    setIsLoading(false);
    setHasError(false);

    return () => {
      mountedRef.current = false;
    };
  }, [seedTickers, seedTopics]);

  const ensureLoaded = useCallback(() => {
    if (hasLoadedRef.current || pendingLoadRef.current || !session) return;
    setIsLoading(true);
    setHasError(false);

    const seededTopics = initialTrendingTopics?.length ? initialTrendingTopics : undefined;
    const topicsPromise = seededTopics
      ? Promise.resolve({ ok: seededTopics })
      : session.getManager(TrendingTopicsManager).getTrendingTopics();

    pendingLoadRef.current = topicsPromise
      .then(result => {
        if (!mountedRef.current) return;

        if (result.err) {
          setHasError(true);
          return;
        }

        const topics = result.ok ?? [];
        const tickerTopics = topics.filter(topic => topic.taxonomy === 'tickers');
        const nonTickerTopics = topics.filter(topic => topic.taxonomy !== 'tickers');

        setTrendingTopics(nonTickerTopics);

        if (tickerTopics.length === 0) {
          hasLoadedRef.current = true;
          return;
        }

        setTrendingTickers(tickerTopics.map(topic => ({ name: topic.name, symbol: topic.name }) as Ticker));

        return enrichTrendingTickerLogos(tickerTopics, session as unknown as Session).then(enriched => {
          if (!mountedRef.current) return;
          if (enriched.length > 0) {
            setTrendingTickers(
              enriched.map(ticker => ({ ...ticker, symbol: ticker.symbol ?? ticker.name })) as Ticker[],
            );
          }
          hasLoadedRef.current = true;
        });
      })
      .catch(() => {
        if (mountedRef.current) setHasError(true);
      })
      .finally(() => {
        pendingLoadRef.current = null;
        if (!mountedRef.current) return;
        setIsLoading(false);
      });
  }, [initialTrendingTopics, session]);

  return {
    ensureLoaded,
    hasError,
    isLoading,
    trendingTickers,
    trendingTopics,
  };
};
